import Http from '@/services/http';
import {
  ProfileListRequest,
  ProfileListResponse,
  ProfileTreeRequest,
  ProfileTreeResponse,
  ProfileDeleteRequest,
  ProfileRemoveValidationResponse,
  ProfileConfirmBlockResponse,
  LinkedUsersRequest,
  LinkedUsersResponse,
} from 'sigma-mappings';

/**
 * Callback para solicitar confirmação de bloqueio de um perfil.
 * @callback ConfirmBlockCallback
 * @param {Number} usersWithUniqueProfile Quantidade de usuários que possuem este perfil como único.
 * @param {Function} continue Callback para continuar, concretizando a confirmação.
 */

/**
 * Callback de confirmação.
 * @callback ConfirmCallback
 * @param {*} Params Parametros para confirmação.
 * @return {Promise} Promessa para responder a confirmação.
 */

const urls = {
  getProfiles: '/perfil/listagem',
  confirmDeleteProfile: '/Perfil/Excluir/Validar',
  deleteProfile: '/Perfil/deletar',
  confirmBlockProfile: '/Perfil/bloqueio/validar/{id}',
  blockProfile: '/Perfil/bloqueio/confirmar/{id}',
  unblockProfile: '/Perfil/desbloqueio/{id}',
  getProfilesTree: '/Perfil/hierarquia',
  getUserFromProfile: profileId => `/perfil/${profileId}/usuarios`,
  getUserFromProfiles: '/perfil/usuarios',
};

export default {
  /**
   * Obtem a lista de perfis. Trabalha com requisição e respostas paginadas.
   * @param {PagedRequest} pagedRequest
   * @param {*} pagedRequest.filter Objeto de filtros da requisição.
   */
  getProfiles({
    page, pageSize, sort, filter,
  }) {
    const http = new Http();
    return http.post(urls.getProfiles, new ProfileListRequest({
      page,
      pageSize,
      sort,
      filter,
    })).then(response => new ProfileListResponse(response.data));
  },
  /**
   * Obtem a hierarquia de perfis.
   */
  getProfilesTree({ name }) {
    const http = new Http();
    return http.post(urls.getProfilesTree, new ProfileTreeRequest({ name }))
      .then(({ data }) => data.map(item => new ProfileTreeResponse(item)));
  },
  /**
   * Apaga os perfis informados. Precisa de um processo de confirmação.
   * @param {String} ids
   * @param {ConfirmCallback} confirmCallback
   */
  deleteProfiles(ids, confirmCallback) {
    const http = new Http();
    return http.put(urls.confirmDeleteProfile, ids)
      .then(({ data }) => confirmCallback(
        new ProfileRemoveValidationResponse(data),
      ))
      .then(() => {
        const params = new ProfileDeleteRequest({ ids });
        http
          .post(urls.deleteProfile, params);
      });
  },
  /**
   * Bloqueia o perfil informado. Precisa de um processo de confirmação.
   * @param {String} profileId
   * @param {ConfirmCallback} confirmCallback
   */
  blockProfile(profileId, confirmCallback) {
    const http = new Http();
    return http.get(urls.confirmBlockProfile.replace(/\{id\}/g, profileId))
      .then(({ data }) => confirmCallback(
        new ProfileConfirmBlockResponse(data),
      ))
      .then(() => http.post(urls.blockProfile.replace(/\{id\}/g, profileId)));
  },
  /**
   * Desbloqueia o perfil informado. Precisa de um processo de confirmação.
   * @param {Number} profileId
   */
  unblockProfile(profileId) {
    const http = new Http();
    return http.post(urls.unblockProfile.replace(/\{id\}/g, profileId));
  },
  /**
   * Retorna a lista de usuários vinculados aos perfis informados.
   * @param {String[]} profileIds
   */
  getLinkedUsers(profileIds, onlyUsersWithUniqueProfile = false) {
    const http = new Http();
    const params = new LinkedUsersRequest({ profileIds, onlyUsersWithUniqueProfile });
    return http.post(urls.getUserFromProfiles, params)
      .then(({ data }) => data.map(item => new LinkedUsersResponse(item)));
  },
};
